import { useDispatch, useSelector } from "react-redux";
import { useTablePagination } from "../../hooks/useTablePagination";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeCustomerList,
} from "../../redux/counterSlice";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Switch,
} from "@mui/material";
import { customerDiscountSchema } from "../../app/schema";
import Loader from "../../components/loader/Loader";
import MUIDataTable from "mui-datatables";
import { FormInputDropdown } from "../../components/form-components/FormInputDropdown";
import EditIcon from "@mui/icons-material/Edit";
import { grey } from "@mui/material/colors";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { FormAutoComplete } from "../../components/form-components/FormAutoComplete";
import { usePermission } from "../../hooks/usePermission";

function CategoryDiscount() {
  const dispatch = useDispatch();
  const { taxList, categoryList, confirmModal } = useSelector(
    (state) => state.counter,
  );
  const permissions = usePermission("customer Discount");
  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("category-discount", 10);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(customerDiscountSchema) });

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [discountList, setDiscountList] = useState([]);
  const [customerDropDownList, setCustomerDropDownList] = useState([]);
  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Fetch only discount customer list
  const fetchDiscountCustomerList = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllDiscountCustomerList");
      if (response?.data?.data?.length > 0) {
        setFormData(response.data.data);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  // Fetch discount list and customer dropdown list
  const fetchDiscountAndCustomerLists = async () => {
    try {
      setLoader(true);
      const [allActiveDiscountList, customerList] = await axios.all([
        axios.get("/getAllActiveDiscountList"),
        axios.get("/getAllCustomerDetails"),
      ]);
      if (allActiveDiscountList?.data?.data?.length > 0) {
        setDiscountList(allActiveDiscountList.data.data);
      }
      if (customerList?.data?.data?.length > 0) {
        const customers = customerList.data.data;

        setCustomerDropDownList(customers);
        dispatch(storeCustomerList(customers));
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  // Initial load: fetch all lists
  const loadInitialData = async () => {
    await Promise.all([
      fetchDiscountCustomerList(),
      fetchDiscountAndCustomerLists(),
    ]);
  };

  useEffect(() => {
    (async () => {
      await loadInitialData();
    })();
  }, [page, rowsPerPage]);

  const toggleStatus = (id, status) => {
    setLoader(true);
    const url =
      status === 1
        ? `/deActivateDiscountCustomer/${id}`
        : `/activateDiscountCustomer/${id}`;
    axios
      .get(url)
      .then(fetchDiscountCustomerList)
      .catch((err) => toast.error(err.message))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal());
      });
  };

  const deleteClickHandler = async (id) => {
    setLoader(true);
    try {
      const { data } = await axios.get(`/deleteDiscountCustomer/${id}`);
      if (!data.error) {
        toast.success(data.message);
        fetchDiscountCustomerList();
      } else {
        dispatch(showModal({ msg: data.message, error: true }));
      }
    } catch (err) {
      dispatch(showModal({ msg: err.message, error: true }));
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal());
    }
  };

  const saveForm = async (values) => {
    setLoader(true);
    const payload = {
      customer: values?.customer?.map((item) => item.value).join(","),
      discount_id: values?.discount_id,
    };
    try {
      const res = selectedRow?.customer_dis_id
        ? await axios.post(`/updateDiscountCustomer`, {
            customer_dis_id: selectedRow?.customer_dis_id,
            ...payload,
          })
        : await axios.post("/createDiscountCustomer", payload);

      if (!res.data.error) {
        toast.success(res.data.message);
        fetchDiscountCustomerList();
        reset();
        setSelectedRow(null);
      } else {
        dispatch(showModal({ msg: res.data.message, error: true }));
      }
    } catch (err) {
      dispatch(showModal({ msg: err.message, error: true }));
    } finally {
      setLoader(false);
      setOpen(false);
    }
  };

  const openConfirmation = (item, action, label) => {
    setSelectedRow(item);
    dispatch(
      showConfirmModal({
        action: () => action(item.customer_dis_id || item.id),
        confirmLabel: label,
      }),
    );
  };

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{+dataIndex + 1}</p>;
        },
      },
    },
    // {
    //   name: "discount_name",
    //   label: "Discount Name",
    //   options: {
    //     customBodyRenderLite: (dataIndex) => {
    //       return <p>{formData[dataIndex]?.discount?.discount_name}</p>;
    //     },
    //   },
    // },

    {
      name: "discount_percentage",
      label: "Percentage",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <p>{formData[dataIndex]?.discount?.[0]?.discount_percentage}%</p>
          );
        },
      },
    },
    {
      name: "customers",
      label: "Customers",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const customers = formData[dataIndex]?.customer || [];
          const maxPills = 4;
          const pills = customers.slice(0, maxPills).map((customer, idx) => (
            <span
              key={customer.customer_id || customer.customer_name || idx}
              style={{
                display: "inline-block",
                background: "#e0e7ff",
                color: "#3730a3",
                borderRadius: "16px",
                padding: "2px 10px",
                marginRight: "4px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {customer.customer_name}
            </span>
          ));
          const extraCount = customers.length - maxPills;
          const allNames = customers.map((c) => c.customer_name).join(", ");
          return (
            <span>
              {pills}
              {extraCount > 0 && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#fca5a5",
                    color: "#991b1b",
                    borderRadius: "16px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  title={allNames}
                >
                  +{extraCount} more
                </span>
              )}
            </span>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          const id = formData[dataIndex]?.customer_dis_id;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => {
                const confirmLabel = status === 1 ? "Deactivate" : "Activate";
                const action = () => toggleStatus(id, status);

                setSelectedRow({
                  ...formData[dataIndex],
                  action,
                  confirmLabel,
                });

                dispatch(showConfirmModal({ message: confirmLabel, action }));
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },
    ...(permissions?.edit
      ? [
          {
            name: "Edit",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                const item = formData[dataIndex];
                return (
                  <div className="actionBtns">
                    <Button
                      disabled={status === 1 ? true : false}
                      aria-label="edit"
                      onClick={() => {
                        setSelectedRow(item);
                        setOpen(true);
                      }}
                    >
                      <EditIcon
                        fontSize="medium"
                        sx={{ color: status === 1 ? grey[600] : "secondary" }}
                      />
                    </Button>
                  </div>
                );
              },
            },
          },
        ]
      : []),
    ...(permissions?.delete
      ? [
          {
            name: "Delete",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                const item = formData[dataIndex];
                return (
                  <div className="actionBtns">
                    <Button
                      disabled={status === 1 ? true : false}
                      aria-label="edit"
                      onClick={() =>
                        openConfirmation(item, deleteClickHandler, "Delete")
                      }
                    >
                      <DeleteOutlineOutlinedIcon
                        color="error"
                        fontSize="medium"
                        sx={{ color: status === 1 ? grey[600] : "red" }}
                      />
                    </Button>
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];
  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    page,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: searchText,
  };

  console.log(selectedRow);
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4>Customer Discount</h4>
          <MUIDataTable
            title={
              permissions.add ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedRow(null);
                    setOpen(true);
                    reset();
                  }}
                >
                  New Customer Discount
                </Button>
              ) : null
            }
            data={formData}
            columns={columns}
            options={options}
          />
        </>
      )}
      {/* Edit Dialog */}
      {/* <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={() => {
          reset();
          setOpen(false);
        }}
      >
        <DialogTitle>
          {selectedRow ? "Edit" : "New"} Customer Discount
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormAutoComplete
                name="customer"
                control={control}
                label="Customer"
                multiple={true}
                dataArray={
                  customerDropDownList?.map((item) => ({
                    value: item?.customer_id,
                    label: item?.customer_name,
                    key: item?.customer_id,
                  })) || []
                }
                errors={errors}
                defaultValue={
                  selectedRow?.customer?.map((item) => ({
                    value: item?.customer_id,
                    label: item?.customer_name,
                    key: item?.customer_id,
                  })) || []
                }
                mandatory="true"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputDropdown
                name="discount_id"
                control={control}
                label="Discount"
                type="single"
                options={discountList?.map((item) => ({
                  id: item?.discount_id,
                  name: `${item?.discount_name} (${item?.discount_percentage}%)`,
                  key: item?.discount_id,
                }))}
                errors={errors}
                defaultValue={selectedRow?.discount_id}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit(saveForm)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog> */}
      <Dialog
        open={open}
        maxWidth="md" // Changed to 'md' for better width with enhanced info
        fullWidth
        onClose={() => {
          reset();
          setOpen(false);
        }}
      >
        <DialogTitle>
          {selectedRow ? "Edit" : "New"} Customer Discount
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormAutoComplete
                name="customer"
                control={control}
                label="Customer"
                multiple={true}
                dataArray={
                  customerDropDownList?.map((item) => ({
                    value: item?.customer_id,
                    label: `${item?.customer_name} - ${item?.customer_mobile}`,
                    key: item?.customer_id,
                  })) || []
                }
                errors={errors}
                defaultValue={
                  selectedRow?.customer?.map((item) => ({
                    value: item?.customer_id,
                    label: `${item?.customer_name} - ${item?.customer_mobile}`,
                    key: item?.customer_id,
                  })) || []
                }
                mandatory="true"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputDropdown
                name="discount_id"
                control={control}
                label="Discount"
                type="single"
                options={discountList?.map((item) => ({
                  id: item?.discount_id,
                  name: `${item?.discount_name} (${item?.discount_percentage}%)`,
                  key: item?.discount_id,
                }))}
                errors={errors}
                defaultValue={selectedRow?.discount_id}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit(saveForm)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmModal?.flag}
        onClose={() => dispatch(hideConfirmModal({ msg: "" }))}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedRow?.confirmLabel}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(hideConfirmModal())}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              confirmModal.action();
              dispatch(hideConfirmModal());
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CategoryDiscount;
